import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * DLP (Data Loss Prevention) Component
 * - Disables right-click context menu
 * - Disables text selection on sensitive content
 * - Adds watermark overlay with user info
 * - Prevents screenshots (partial, OS-level can't be blocked)
 */
export function DLPProvider({ children, enableWatermark = true, enableRightClickProtection = true }) {
  const { user } = useAuth();

  useEffect(() => {
    if (!enableRightClickProtection) return;

    // Disable right-click context menu
    const handleContextMenu = (e) => {
      // Allow right-click on input fields for paste functionality
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      e.preventDefault();
      return false;
    };

    // Disable common keyboard shortcuts for copying/saving
    const handleKeyDown = (e) => {
      // Allow in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Ctrl+S (Save), Ctrl+P (Print), Ctrl+Shift+I (DevTools)
      if ((e.ctrlKey || e.metaKey) && ['s', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      
      // F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableRightClickProtection]);

  return (
    <div className="relative">
      {/* Content */}
      {children}
      
      {/* Watermark Overlay */}
      {enableWatermark && user && (
        <div 
          className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden select-none"
          aria-hidden="true"
        >
          <div className="absolute inset-0" style={{
            background: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 200px,
              rgba(0,0,0,0.02) 200px,
              rgba(0,0,0,0.02) 400px
            )`
          }}>
            {/* Generate multiple watermarks across the screen */}
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="absolute text-gray-300/30 text-sm font-mono whitespace-nowrap transform -rotate-45"
                style={{
                  top: `${(i * 25) % 100}%`,
                  left: `${(i * 30) % 100}%`,
                  fontSize: '11px',
                  userSelect: 'none',
                  pointerEvents: 'none'
                }}
              >
                {user.email} • {new Date().toLocaleDateString()}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Hook to add DLP protection to specific elements
 */
export function useDLP() {
  const disableSelection = (element) => {
    if (element) {
      element.style.userSelect = 'none';
      element.style.webkitUserSelect = 'none';
      element.style.msUserSelect = 'none';
    }
  };

  const enableSelection = (element) => {
    if (element) {
      element.style.userSelect = 'auto';
      element.style.webkitUserSelect = 'auto';
      element.style.msUserSelect = 'auto';
    }
  };

  return { disableSelection, enableSelection };
}

export default DLPProvider;
